import styled from '@emotion/styled'
import { cloneElement, FC, PropsWithChildren, ReactElement, useLayoutEffect, useRef, useState } from 'react'
import { PopoverAlign, PopoverPosition, Popover as ReactTinyPopover } from 'react-tiny-popover'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { BorderRadius, ElevationLevelValues } from '~/assets/style/tokens'
import { WithClassName } from '~/types/utils'

const PopoverContent = styled.div<{ popoverWidth: number | undefined }>`
  overflow: hidden;
  background: ${Colors.White};
  ${Fonts.colors.Jet}
  padding: 0;
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(54, 54, 54, 0.3);
  ${Fonts.P1}
  min-width: ${({ popoverWidth }) => (popoverWidth ? `${popoverWidth}px` : 'max-content')};
`

export const PopoverTriggerWrapper = styled.div`
  display: inline-block;
`

type PopoverProps = WithClassName & {
  trigger: ReactElement<{ isOpened?: boolean; className?: string }>
  align?: PopoverAlign
  open?: boolean
  setOpen?: (_: boolean) => void
  positions?: PopoverPosition[]
  isToggleable?: boolean
}
const BasePopover: FC<PropsWithChildren<PopoverProps>> = ({ className, children, trigger, align, open, setOpen, positions, isToggleable }) => {
  const [internalOpen, setInternalOpen] = useState(open || false)
  const [previousOpen, setPreviousOpen] = useState(open)

  // `open` drives the popover from the parent: realign on change, during render rather than in an effect
  if (previousOpen !== open) {
    setPreviousOpen(open)
    setInternalOpen(open || false)
  }

  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined)
  const triggerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (internalOpen && triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth)
    }
  }, [internalOpen])

  return (
    <ReactTinyPopover
      containerStyle={{ zIndex: `${ElevationLevelValues.high}` }}
      isOpen={internalOpen}
      content={
        <PopoverContent popoverWidth={popoverWidth} className={className}>
          {children}
        </PopoverContent>
      }
      positions={positions?.length ? positions : ['bottom', 'top', 'right', 'left']}
      onClickOutside={() => (setOpen || setInternalOpen)(false)}
      /* Capture, because react-tiny-popover listens on `body`: in the bubble phase an ancestor that
         stops the click — a flyout body does — keeps it from ever arriving. The trigger and the content
         are excluded upstream, so this does not close on its own click. */
      clickOutsideCapture
      align={align ?? 'start'}
    >
      <PopoverTriggerWrapper onClick={() => (setOpen || setInternalOpen)(isToggleable ? !open : true)}>
        <div ref={triggerRef}>{cloneElement(trigger, { isOpened: open || internalOpen })}</div>
      </PopoverTriggerWrapper>
    </ReactTinyPopover>
  )
}
const Popover = styled(BasePopover)``

export const PopoverContentWrapper = styled.div`
  ${BorderRadius.style}
  display: flex;
  flex-direction: column;
`

export const PopoverLine = styled.div`
  ${Fonts.colors.Hurricane}
  background: ${Colors.White};
  padding: 0.5rem 1.375rem 0.5rem 0.75rem;
  display: inline-flex;
  gap: 0.25rem;
  user-select: none;
  cursor: pointer;
  align-items: center;

  &:hover {
    background: ${Colors.Topaze};
  }
`

export { Popover }
